import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Clock,
  User,
  Lock,
  Eye,
  EyeOff,
  MessageCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import { useAuth } from '@context/AuthContext';
import Button from '@components/common/Button';

const TicketComments = ({ ticketId, comments = [] }) => {
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [timeLogged, setTimeLogged] = useState('');
  const [showInternal, setShowInternal] = useState(true);
  const [charCount, setCharCount] = useState(0);

  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Add comment mutation
  const addCommentMutation = useMutation(
    async (commentData) => {
      const resp = await apiHelper.post(`/tickets/${ticketId}/comments`, commentData);
      return resp.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket', ticketId]);
        setNewComment('');
        setTimeLogged('');
        setIsInternal(false);
        setCharCount(0);
        toast.success('Comment added successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add comment');
      }
    }
  );

  // Helpers
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const formatTimeLogged = (minutes) => {
    if (!minutes) return null;
    const m = Number(minutes);
    if (m === 0) return null;
    if (m < 60) return `${m}m`;
    const hours = Math.floor(m / 60);
    const rem = m % 60;
    return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
  };

  // Filter comments based on user + toggle
  const filteredComments = comments.filter((c) => {
    if (!c) return false;
    if (user?.role === 'customer' || user?.role === 'supplier') {
      return !c.is_internal;
    }
    // internal users: show internal based on toggle
    return showInternal ? true : !c.is_internal;
  });

  // Form handling
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    const payload = {
      comment: newComment.trim(),
      is_internal: !!isInternal,
      time_logged_minutes: timeLogged ? Number(timeLogged) : 0
    };

    addCommentMutation.mutate(payload);
  };

  // Ctrl/Cmd + Enter to submit
  const handleKeyDown = useCallback(
    (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        handleSubmit(e);
      }
    },
    [newComment, isInternal, timeLogged]
  );

  useEffect(() => {
    setCharCount(newComment.length);
  }, [newComment]);

  // Determine if current user is internal staff (can toggle internal notes)
  const isInternalUser = user?.role === 'admin' || user?.role === 'user';

  return (
    <div className="space-y-8">
      {/* Comment Form */}
      <motion.div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-gray-600" />
            Add Comment
          </h4>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6" onKeyDown={handleKeyDown}>
            <div>
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                Comment
              </label>
              <textarea
                id="comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share updates, ask questions, or provide feedback... (Ctrl/Cmd+Enter to submit)"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical transition-all placeholder:text-gray-400"
                aria-label="Add comment"
                required
                maxLength={5000}
              />
              <div className="mt-2 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Press Ctrl/Cmd + Enter to submit quickly
                </div>
                <div className="text-xs text-gray-500">
                  {charCount} / 5000 characters
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-6">
                {/* Internal Toggle for staff */}
                {isInternalUser && (
                  <label className="flex items-center gap-3 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      aria-label="Mark as internal note"
                    />
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-500" />
                      Internal note
                    </span>
                  </label>
                )}

                {/* Time logging */}
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder="0"
                      value={timeLogged}
                      onChange={(e) => setTimeLogged(e.target.value.replace(/[^\d]/g, ''))}
                      className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      min="0"
                      aria-label="Time logged in minutes"
                    />
                    <span className="text-sm font-medium text-gray-600">minutes logged</span>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                size="md"
                leftIcon={<Send className="w-4 h-4" />}
                loading={addCommentMutation.isLoading}
                disabled={addCommentMutation.isLoading || !newComment.trim()}
                className="px-6"
              >
                {addCommentMutation.isLoading ? 'Adding...' : 'Add Comment'}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Header and View Options */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
          <MessageCircle className="w-6 h-6 text-blue-600" />
          Comments 
          <span className="ml-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            {filteredComments.length}
          </span>
        </h3>

        {isInternalUser && (
          <button
            onClick={() => setShowInternal(!showInternal)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-all ${
              showInternal 
                ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' 
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
            aria-pressed={showInternal}
            aria-label={showInternal ? 'Hide internal comments' : 'Show internal comments'}
          >
            {showInternal ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showInternal ? 'Hide internal notes' : 'Show internal notes'}
          </button>
        )}
      </div>

      {/* Comments List */}
      <div className="space-y-4">
        <AnimatePresence>
          {filteredComments.length > 0 ? (
            filteredComments.map((comment, index) => (
              <motion.div
                key={comment.id || `${index}-${comment.created_at}`}
                className={`rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-all ${
                  comment.is_internal 
                    ? 'bg-amber-50 border-amber-200 shadow-amber-100/50' 
                    : 'bg-white border-gray-200'
                }`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18, delay: index * 0.02 }}
              >
                {/* Comment Header */}
                <div className={`px-4 py-3 border-b ${
                  comment.is_internal ? 'bg-amber-100/50 border-amber-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        comment.is_internal ? 'bg-amber-200' : 'bg-blue-100'
                      }`}>
                        <User className={`w-5 h-5 ${comment.is_internal ? 'text-amber-700' : 'text-blue-600'}`} />
                      </div>

                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">
                            {comment.user?.first_name} {comment.user?.last_name}
                          </span>

                          {comment.is_internal && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-md text-xs font-medium">
                              <Lock className="w-3 h-3" />
                              Internal Note
                            </span>
                          )}

                          {comment.is_system_generated && (
                            <span className="inline-flex items-center px-2 py-1 bg-gray-200 text-gray-700 rounded-md text-xs font-medium">
                              System
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                          <span className="font-medium">{formatTimeAgo(comment.created_at)}</span>
                          {comment.time_logged_minutes > 0 && (
                            <>
                              <span>•</span>
                              <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">
                                <Clock className="w-3 h-3" />
                                <span className="font-medium">{formatTimeLogged(comment.time_logged_minutes)} logged</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comment Content */}
                <div className="p-4">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-800 leading-relaxed whitespace-pre-wrap m-0">{comment.comment}</p>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div className="text-center py-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No comments yet</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Be the first to add context, updates, or feedback to this ticket. Your comments help track progress and improve collaboration.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TicketComments;
